/**
 * PincodeInput — drop-in replacement for a plain pincode/zipcode Input.
 * Automatically populates City, State, Country via free postal APIs.
 *
 * Props:
 *   value        — current pincode string
 *   onChange     — (value: string) => void
 *   onAutoFill   — ({ city, state, country }) => void  ← called on successful lookup
 *   placeholder  — optional string
 *   disabled     — optional boolean
 *   label        — optional string (if omitted, no label rendered)
 *   required     — optional boolean (adds asterisk to label)
 *   id           — optional string for the input id
 */
import React from 'react';
import { Input } from './ui/input';
import { usePincodeAutoFill } from '../hooks/usePincodeAutoFill';
import { Loader2, CheckCircle2, MapPin } from 'lucide-react';

export function PincodeInput({
  value = '',
  onChange,
  onAutoFill,
  placeholder = 'Enter pincode / zipcode',
  disabled = false,
  label,
  required = false,
  id = 'pincode',
  className = '',
}) {
  const { lookup, loading, error, detected } = usePincodeAutoFill();

  function handleChange(e) {
    const v = e.target.value;
    onChange(v);
    lookup(v, onAutoFill);
  }

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          id={id}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8 pr-8"
          autoComplete="postal-code"
          data-testid={id}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-blue-500" />
        )}
        {!loading && detected && (
          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-green-500" />
        )}
      </div>
      {detected && !error && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Auto-filled: {[detected.city, detected.state, detected.country].filter(Boolean).join(', ')}
        </p>
      )}
      {error && (
        <p className="text-xs text-amber-600">{error}</p>
      )}
    </div>
  );
}
